// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import { convertDateToDateOnlyString } from "@insite/client-framework/Common/Utilities/DateUtilities";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Accordion, { AccordionPresentationProps } from "@insite/mobius/Accordion";
import { AccordionSectionPresentationProps } from "@insite/mobius/AccordionSection";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DatePicker, { DatePickerPresentationProps, DatePickerState } from "@insite/mobius/DatePicker";
import GridContainer, { GridContainerProps, GridOffset } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { AccordionSection } from "@insite/mobius/index";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import Tag, { horizontalStyles, TagPresentationProps } from "@insite/mobius/Tag";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import clearSearch from "@rc/App/Handlers/Pages/BackOrderList/ClearSearch";
import toggleFiltersOpen from "@rc/App/Handlers/Pages/BackOrderList/ToggleFiltersOpen";
import updateSearchFields from "@rc/App/Handlers/Pages/BackOrderList/UpdateSearchFields";
import { CustomAppState } from "@rc/App/Store/Reducers";
import { color, xScreen } from "@rc/XDL/Utilities";
import * as React from "react";
import { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    isFilterOpen: state.xBackOrderListState.isFilterOpen,
    getBackOrderApiParameter: state.xBackOrderListState.getBackOrderApiParameter,
});

const mapDispatchToProps = {
    updateSearchFields,
    clearSearch,
    toggleFiltersOpen,
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface BackOrderListFilterStyles {
    container?: GridContainerProps;
    headingGridItem?: GridItemProps;
    heading?: TypographyPresentationProps;
    datesGridItem?: GridItemProps;
    datesContainer?: GridContainerProps;
    dateRangeFieldset?: InjectableCss;
    fromGridItem?: GridItemProps;
    fromDate?: DatePickerPresentationProps;
    toGridItem?: GridItemProps;
    toDate?: DatePickerPresentationProps;
    orderTotalGridItem?: GridItemProps;
    filterLeftSideContainer?: GridContainerProps;
    orderTotalFieldset?: InjectableCss;
    skuGridItem?: GridItemProps;
    orderTotalOperatorSelect?: SelectPresentationProps;
    orderNumberGridItem?: GridItemProps;
    skuTextField?: TextFieldPresentationProps;
    orderNumberTextField?: TextFieldPresentationProps;
    appliedFiltersContainer?: InjectableCss;
    appliedFilterTag?: TagPresentationProps;
    buttonsItem?: GridItemProps;
    clearFiltersButton?: ButtonPresentationProps;

    accordion?: AccordionPresentationProps;
    accordionSection?: AccordionSectionPresentationProps;
}

export const backOrderListFilterStyles: BackOrderListFilterStyles = {
    container: {
        css: css`
            padding-bottom: 15px;
        `,
    },
    headingGridItem: {
        width: 12,
        css: css`
            padding-bottom: 0;
        `,
    },
    heading: {
        variant: "h5",
        css: css`
            margin: 0;
        `,
    },
    datesGridItem: {
        css: css`
            padding: 15px 10px;
        `,
        width: [12, 12, 5, 5, 5],
    },
    datesContainer: {
        gap: 10,
        css: css`
            & ${GridOffset} {
                width: calc(100% + 10px);

                .react-datetime-picker {
                    min-width: initial;
                }
            }
        `,
    },
    dateRangeFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    fromGridItem: {
        width: 6,
        css: css`
            ${xScreen("gte", 'sm')} {
                padding: 5px 10px;
            }
            > div {
                width: 100%;
            }
        `,
    },
    toGridItem: {
        width: 6,
        css: css`
            ${xScreen("gte", 'sm')} {
                padding: 5px 10px;
            }
            > div {
                width: 100%;
            }
        `,
    },
    appliedFiltersContainer: { css: horizontalStyles },
    buttonsItem: {
        width: 12,
        css: css`
            justify-content: flex-end;
        `,
    },
    clearFiltersButton: {
        variant: "secondary",
        sizeVariant: "small",
        css: css`
            margin-right: 10px;
        `,
    },
    toDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },
    orderTotalGridItem: {
        css: css`
            padding: 15px 10px;
        `,
        width: [12, 12, 7, 7, 7],
    },
    filterLeftSideContainer: {
        gap: 10,
        css: css`
            & ${GridOffset} {
                width: calc(100% + 10px);
            }
        `,
    },
    orderTotalFieldset: {
        css: css`
            border: none;
            margin: 0;
            padding: 0;
            width: 100%;
        `,
    },
    skuGridItem: {
        width: 6,
        css: css`
            ${xScreen("gte", 'sm')} {
                padding: 5px 10px;
            }
            > div {
                width: 100%;
            }
        `,
    },
    orderNumberGridItem: {
        width: 6,
        css: css`
            ${xScreen("gte", 'sm')} {
                padding: 5px 10px;
            }
            > div {
                width: 100%;
                 
            }
        `,
    },
    orderNumberTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 24px;
            `,
        },
    },
    skuTextField: {
        cssOverrides: {
            formInputWrapper: css`
                margin-top: 24px;
            `,
        },
    },
    fromDate: {
        cssOverrides: {
            formField: css`
                width: 100%;
            `,
        },
    },

    accordion: {
        css: css`
            width: 100%;
        `,
    },
    accordionSection: {
        panelProps: {
            css: css`
                padding: 0;
            `,
        },
        headerProps: {
            css: css`
                &&& button,
                &&& button:focus {
                    padding: 21px 56px 20px 8px;
                    border: initial; /* removing OOTB border */
                    border-bottom: 1px solid ${color("common.border")};
                    margin-bottom: 20px;
                }
            `,
        },
    },
};

const StyledDateRangeFieldSet = getStyledWrapper("fieldset");
const StyledOrderTotalFieldSet = getStyledWrapper("fieldset");

export const styles = backOrderListFilterStyles;
let updateTimeoutId: any;

const BackOrderListFilter = ({
    isFilterOpen,
    getBackOrderApiParameter,
    updateSearchFields,
    clearSearch,
    toggleFiltersOpen,
}: Props) => {
    const [backOrderSku, setBackOrderSku] = React.useState("");
    const [backOrderNumber, setBackOrderNumber] = React.useState("");

    useEffect(() => {
        if (
            !backOrderSku &&
            !backOrderNumber &&
            getBackOrderApiParameter.sku &&
            getBackOrderApiParameter.orderNumber
        ) {
            setBackOrderSku(getBackOrderApiParameter.sku);
            setBackOrderNumber(getBackOrderApiParameter.orderNumber);
        }
        toggleFiltersOpen();
    }, []);

    const backOrderSkuValueChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const sku = event.target.value;
        setBackOrderSku(sku);

        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                sku,
            });
        }, 350);
    };

    const backOrderOrderNumberValueChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        const orderNumber = event.target.value;
        setBackOrderNumber(orderNumber);

        if (typeof updateTimeoutId === "number") {
            clearTimeout(updateTimeoutId);
        }

        updateTimeoutId = setTimeout(() => {
            updateSearchFields({
                orderNumber,
            });
        }, 350);
    };

    const fromDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            fromDate: selectedDay ? selectedDay.toISOString() : "",
        });
    };

    const toDateChangeHandler = ({ selectedDay }: Pick<DatePickerState, "selectedDay">) => {
        updateSearchFields({
            toDate: selectedDay ? selectedDay.toISOString() : "",
        });
    };

    const resetBackOrderSku = () => {
        setBackOrderSku("");
        updateSearchFields({ sku: "" });
    };

    const resetBackOrderNumber = () => {
        setBackOrderNumber("");
        updateSearchFields({ orderNumber: "" });
    };

    const clearFiltersClickHandler = () => {
        clearSearch();
        setBackOrderSku("");
        setBackOrderNumber("");
    };

    const fromDate = getBackOrderApiParameter.fromDate ? new Date(getBackOrderApiParameter.fromDate) : undefined;
    const toDate = getBackOrderApiParameter.toDate ? new Date(getBackOrderApiParameter.toDate) : undefined;

    const tagsApplied = (
        <StyledWrapper>
            {!isFilterOpen && (
                <StyledWrapper {...styles.appliedFiltersContainer}>
                    {getBackOrderApiParameter.fromDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                fromDateChangeHandler({});
                            }}
                        >
                            {`${translate("From")}: ${convertDateToDateOnlyString(fromDate)}`}
                        </Tag>
                    )}
                    {getBackOrderApiParameter.toDate && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                toDateChangeHandler({});
                            }}
                        >
                            {`${translate("To")}: ${convertDateToDateOnlyString(toDate)}`}
                        </Tag>
                    )}
                    {getBackOrderApiParameter.sku && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                resetBackOrderSku();
                            }}
                        >
                            {`${translate("SKU")}: ${getBackOrderApiParameter.sku}`}
                        </Tag>
                    )}
                    {getBackOrderApiParameter.orderNumber && (
                        <Tag
                            {...styles.appliedFilterTag}
                            onDelete={() => {
                                resetBackOrderNumber();
                            }}
                        >
                            {`${translate("Order #")}: ${getBackOrderApiParameter.orderNumber}`}
                        </Tag>
                    )}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );

    return (
        <StyledWrapper>
            {tagsApplied}
            <Accordion headingLevel={4} {...styles.accordion}>
                <AccordionSection
                    {...styles.accordionSection}
                    title={translate("Filter Results")}
                    onTogglePanel={() => toggleFiltersOpen()}
                >
                    <GridContainer {...styles.container}>
                        <GridItem {...styles.orderTotalGridItem}>
                            <StyledOrderTotalFieldSet {...styles.orderTotalFieldset}>
                                <VisuallyHidden as="label">{translate("SKU")}</VisuallyHidden>
                                <GridContainer {...styles.filterLeftSideContainer}>
                                    <GridItem {...styles.skuGridItem}>
                                        <TextField
                                            {...styles.skuTextField}
                                            label={translate("SKU")}
                                            value={backOrderSku}
                                            type="text"
                                            data-test-selector="backOrderList_skuValue"
                                            onChange={backOrderSkuValueChangeHandler}
                                        />
                                    </GridItem>
                                    <GridItem {...styles.orderNumberGridItem}>
                                        <TextField
                                            {...styles.orderNumberTextField}
                                            label={translate("Order #")}
                                            value={backOrderNumber}
                                            type="text"
                                            maxLength={100}
                                            data-test-selector="backOrderList_orderNumberValue"
                                            onChange={backOrderOrderNumberValueChangeHandler}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </StyledOrderTotalFieldSet>
                        </GridItem>
                        <GridItem {...styles.datesGridItem}>
                            <StyledDateRangeFieldSet {...styles.dateRangeFieldset}>
                                <VisuallyHidden as="label">{translate("Date Range")}</VisuallyHidden>
                                <GridContainer {...styles.datesContainer}>
                                    <GridItem
                                        data-test-selector="backOrderList_fromDateFilter"
                                        {...styles.fromGridItem}
                                    >
                                        <DatePicker
                                            {...styles.fromDate}
                                            label={translate("Date Range")}
                                            selectedDay={fromDate}
                                            onDayChange={fromDateChangeHandler}
                                            dateTimePickerProps={{
                                                clearIcon: null,
                                                maxDate: toDate,
                                                ...styles.fromDate?.dateTimePickerProps,
                                            }}
                                        />
                                    </GridItem>
                                    <GridItem data-test-selector="backOrderList_toDateFilter" {...styles.toGridItem}>
                                        <DatePicker
                                            {...styles.toDate}
                                            label={translate("To")}
                                            selectedDay={toDate}
                                            onDayChange={toDateChangeHandler}
                                            dateTimePickerProps={{
                                                clearIcon: null,
                                                minDate: fromDate,
                                                ...styles.toDate?.dateTimePickerProps,
                                            }}
                                        />
                                    </GridItem>
                                </GridContainer>
                            </StyledDateRangeFieldSet>
                        </GridItem>
                        <GridItem {...styles.buttonsItem}>
                            <Button
                                {...styles.clearFiltersButton}
                                data-test-selector="backOrderList_clearFiltersButton"
                                onClick={clearFiltersClickHandler}
                            >
                                {translate("Clear")}
                            </Button>
                        </GridItem>
                    </GridContainer>
                </AccordionSection>
            </Accordion>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BackOrderListFilter),
    definition: {
        group: "XNGAGE" as any,
        displayName: "BackOrder - Search Results Filter",
    },
};

export default widgetModule;
